import {subscribeToMidiEvents} from "../eventBus/midi/midiEventBus";
import type {IMidiNotePlayed, MidiEventTypes} from "../eventBus/midi/MidiEventTypes";
import {isDefined} from "../../util/is-type";
import {logger} from "../log/logger";
import {settingsStore} from "../../redux/settings/settingsStore";
import {isNoteAnyB, isNoteAnyC, isNoteAnyD} from "./NoteService";

export type MidiNavigationKeyType = "C" | "D" | "B";

interface IMidiUserActionConfig {
  actionId: string;
  callback: (midiEvent: IMidiNotePlayed) => void;
}

class MidiUserActionService {
  private midiUnsubscribe?: () => void;

  private onNotePlayedActions: Partial<Record<MidiNavigationKeyType, IMidiUserActionConfig>>;

  constructor() {
    this.onNotePlayedActions = {};
  }

  private get isSubscribedToMidi(): boolean {
    return isDefined(this.midiUnsubscribe)
  }

  private get currentMidiActionKeys(){
    return Object.keys(this.onNotePlayedActions);
  }

  private get doesHaveConfiguredMidiActions(): boolean {
    return !!this.currentMidiActionKeys.length;
  }

  private ensureSubscribedToMidiEvents(): void {
    logger.midiAction.debug({
      message: `ensureSubscribedToMidiEvents`,
      data: [
        { key: "isSubscribedToMidi", value: this.isSubscribedToMidi},
      ]
    });

    if (!this.isSubscribedToMidi) {
      this.setupMidiEventSubscription();
    }
  }

  private cleanupMidiEventSubscription(): void {
    logger.midiAction.debug({
      message: `cleanupMidiEventSubscription`,
      data: [
        { key: "doesHaveConfiguredMidiActions", value: this.doesHaveConfiguredMidiActions},
        { key: "currentMidiActionKeys", value: this.currentMidiActionKeys}
      ]
    });

    if (!this.doesHaveConfiguredMidiActions) {
      // no current configured midi actions
      // call unsubscribe if available
      this.removeMidiEventSubscription();
    }
  }

  private removeMidiEventSubscription(): void {
    // make sure not already subscribed
    this.midiUnsubscribe?.();
    this.midiUnsubscribe = undefined;
  }

  private setupMidiEventSubscription(): void {
    logger.midiAction.debug({message: `setupMidiEventSubscription`});
    // make sure not already subscribed
    this.removeMidiEventSubscription();
    // subscribe to midi and save unsubscribe callback
    this.midiUnsubscribe = subscribeToMidiEvents(this.onMidiEventHandler.bind(this), `MidiUserActionService`);
  }

  public onMidiEventHandler(midiEvent: MidiEventTypes): void {
    if (midiEvent.type === "midiNotePlayed") {
      this.onMidiNotePlayedEventHandler(midiEvent);
      return;
    }
  }

  private getMidiNoteFromMidiKeyNumber(midiNumber: number): MidiNavigationKeyType | false {
    if(isNoteAnyB(midiNumber)) {
      return "B";
    }

    if(isNoteAnyC(midiNumber)) {
      return "C";
    }

    if(isNoteAnyD(midiNumber)) {
      return "D";
    }

    // not a configured note
    return false;
  }

  private onMidiNotePlayedEventHandler(midiEvent: IMidiNotePlayed): void {
    const midiKey = this.getMidiNoteFromMidiKeyNumber(midiEvent.number);

    if (midiKey && this.onNotePlayedActions[midiKey]) {
      // Only accept notes from the currently selected midi device
      const selectedMidiDeviceId = settingsStore.getSelectedMidiDeviceId();
      if (midiEvent.normalizedMidiInput.getId() !== selectedMidiDeviceId.toString()) {
        logger.midi.info({
          message: `midi-user-action: Note detected from wrong device. Ignoring. Device expected: ${selectedMidiDeviceId}, device found: ${midiEvent.normalizedMidiInput.getId()}`,
        });
        return;
      }

      this.onNotePlayedActions[midiKey]?.callback(midiEvent);
      this.removeConfiguredMidiAction(midiKey);
    }
  }

  private removeConfiguredMidiAction(midiKey: MidiNavigationKeyType): void {
    // remove configured action
    delete this.onNotePlayedActions[midiKey];

    logger.midiAction.debug({message: `removeConfiguredMidiAction` + midiKey});
    // cleanup midi subscription if not needed
    this.cleanupMidiEventSubscription();
  }

  private generateUniqueActionId(): string {
    const randomString = (Math.random() + 1).toString(36).substring(5);
    const currentTimeMs = Date.now();
    return `${randomString}:${currentTimeMs}`;
  }

  public subscribeToNextMidiNotePlayed(midiKey: MidiNavigationKeyType, callback: (midiEvent: IMidiNotePlayed) => void) {
    this.ensureSubscribedToMidiEvents();
    const actionId = this.generateUniqueActionId();
    const actionIdBefore = this.onNotePlayedActions[midiKey]?.actionId
    this.onNotePlayedActions[midiKey] = {
      actionId,
      callback,
    };

    logger.midiAction.debug({
      message: `subscribeToNextMidiNotePlayed`,
      data: [
        { key: "midiKey", value: midiKey},
        { key: "actionId", value: actionId},
        { key: "actionIdBefore", value: actionIdBefore }
      ]
    });

    return () => this.unsubscribeFromNextMidiNotePlayed(midiKey, actionId);
  }

  public unsubscribeFromNextMidiNotePlayed(midiKey: MidiNavigationKeyType, actionId: string): void {
    const isUnsubscribeForCurrenAction: boolean = this.onNotePlayedActions[midiKey]?.actionId === actionId;

    logger.midiAction.debug({
      message: `unsubscribeFromNextMidiNotePlayed`,
      data: [
        { key: "midiKey", value: midiKey},
        { key: "actionId", value: actionId},
        { key: "isUnsubscribeForCurrenAction", value: isUnsubscribeForCurrenAction }
      ]
    });

    if (!isUnsubscribeForCurrenAction) {
      // not current action, do nothing
      // return to exit
      return;
    }

    this.removeConfiguredMidiAction(midiKey);
  }
}

export const midiUserAction = new MidiUserActionService();
